import { useCallback } from 'react';
import { warnMappingFlow } from '../lib/mappingTrace';
import type { useMappingProjectReadiness } from './useMappingProjectReadiness';
import type { MappingWorkspaceFocusRefs } from './useMappingWorkspaceFocusRefs';

interface UseWorkspaceSaveActionsArgs {
  focusRefs: MappingWorkspaceFocusRefs;
  isQuickFillWorkspaceTab: boolean;
  activeTab: number;
  projectIdentityPending: boolean;
  projectProfileGateBlocksActions: boolean;
  mappingReadiness: Pick<
    ReturnType<typeof useMappingProjectReadiness>,
    'isBlocking' | 'message' | 'status' | 'blockReason'
  >;
  quickFillHasPendingChanges: boolean;
  setSaveError: (error: string | null) => void;
  handleApply: () => Promise<boolean>;
  handleContinue: () => Promise<boolean>;
}

/**
 * Apply/Continue orchestration for the workspace footer. Guards the save with
 * the project-identity, profile-gate, and mapping-readiness blockers, and
 * submits pending Quick Fill edits first when the Quick Fill tab is active.
 */
export const useWorkspaceSaveActions = ({
  focusRefs,
  isQuickFillWorkspaceTab,
  activeTab,
  projectIdentityPending,
  projectProfileGateBlocksActions,
  mappingReadiness,
  quickFillHasPendingChanges,
  setSaveError,
  handleApply,
  handleContinue,
}: UseWorkspaceSaveActionsArgs) => {
  const submitQuickFillBeforeWorkspaceSave = useCallback(async (): Promise<boolean> => {
    if (!isQuickFillWorkspaceTab || !focusRefs.quickFillActions.current) {
      return true;
    }

    return focusRefs.quickFillActions.current.submitChanges();
  }, [focusRefs, isQuickFillWorkspaceTab]);

  const runWorkspaceSaveAction = useCallback(async (
    saveAction: () => Promise<boolean>,
    options: {
      allowQuickFillOnlyWhileMappingBlocked?: boolean;
      allowWhileProjectProfileGateBlocks?: boolean;
    } = {},
  ): Promise<boolean> => {
    if (projectIdentityPending) {
      warnMappingFlow('workspace save blocked', {
        reason: 'projectIdentityPending',
        activeTab,
        isQuickFillWorkspaceTab,
      });
      setSaveError('Waiting for the active Revit project before saving mapping setup.');
      return false;
    }

    // The navigation guard's save is exempt: the profile gate's own auto-confirm
    // is what asked for it, so blocking on the not-yet-confirmed profile would
    // deadlock the gate — the save is the only way through.
    if (projectProfileGateBlocksActions && !options.allowWhileProjectProfileGateBlocks) {
      warnMappingFlow('workspace save blocked', {
        reason: 'projectProfileGateBlocksActions',
        activeTab,
        isQuickFillWorkspaceTab,
      });
      setSaveError('Verify or load a mapping profile for this Revit project before saving.');
      return false;
    }

    const canApplyQuickFillOnlyWhileMappingBlocked =
      Boolean(options.allowQuickFillOnlyWhileMappingBlocked) &&
      isQuickFillWorkspaceTab &&
      mappingReadiness.isBlocking &&
      quickFillHasPendingChanges;

    if (mappingReadiness.isBlocking && !canApplyQuickFillOnlyWhileMappingBlocked) {
      warnMappingFlow('workspace save blocked', {
        reason: 'mappingReadinessBlocking',
        activeTab,
        isQuickFillWorkspaceTab,
        mappingReadinessStatus: mappingReadiness.status,
        mappingReadinessBlockReason: mappingReadiness.blockReason,
        mappingReadinessMessage: mappingReadiness.message,
      });
      setSaveError(mappingReadiness.message || 'Active project mapping setup is not ready yet.');
      return false;
    }

    if (!(await submitQuickFillBeforeWorkspaceSave())) {
      return false;
    }

    if (canApplyQuickFillOnlyWhileMappingBlocked) {
      warnMappingFlow('quick fill apply completed while mapping readiness blocks workspace save', {
        activeTab,
        quickFillHasPendingChanges,
        mappingReadinessBlocking: mappingReadiness.isBlocking,
        mappingReadinessStatus: mappingReadiness.status,
        mappingReadinessBlockReason: mappingReadiness.blockReason,
      });
      return false;
    }

    return saveAction();
  }, [
    activeTab,
    isQuickFillWorkspaceTab,
    mappingReadiness.blockReason,
    mappingReadiness.isBlocking,
    mappingReadiness.message,
    mappingReadiness.status,
    projectIdentityPending,
    projectProfileGateBlocksActions,
    quickFillHasPendingChanges,
    setSaveError,
    submitQuickFillBeforeWorkspaceSave,
  ]);

  const handleWorkspaceApply = useCallback(
    () => runWorkspaceSaveAction(handleApply, { allowQuickFillOnlyWhileMappingBlocked: true }),
    [handleApply, runWorkspaceSaveAction]
  );

  const handleUnsavedChangesSave = useCallback(
    () => runWorkspaceSaveAction(handleApply, { allowWhileProjectProfileGateBlocks: true }),
    [handleApply, runWorkspaceSaveAction]
  );

  const handleWorkspaceContinue = useCallback(
    () => runWorkspaceSaveAction(handleContinue),
    [handleContinue, runWorkspaceSaveAction]
  );

  return {
    handleWorkspaceApply,
    handleUnsavedChangesSave,
    handleWorkspaceContinue,
  };
};
